// Thin client for the App/Tool Registry endpoints (ticket 6). Bearer auth via the shell token,
// matching the rest of the Ros API (see projectsApi.ts). Keeps fetch/JSON plumbing out of the page.
//
// The registry is the admin surface a PM uses to register apps, tools, and public apps that Ros can
// route people to. Registering an `app` also creates its OAuth client on the provider, so the POST
// response for a new app may carry a one-time `clientSecret` the registrant must copy.

export type RegistryKind = 'app' | 'tool' | 'public_app' | 'gadget' | 'external_app'
export type RegistryAudience = 'internal' | 'external' | 'public'

// The launch scopes an app's client may be granted. Mirrors the backend KNOWN_SCOPES set so the form
// offers exactly the scopes the authorize seam will accept.
export const KNOWN_SCOPES = ['projects:read', 'projects:create', 'artifacts:read', 'artifacts:create'] as const
export type KnownScope = (typeof KNOWN_SCOPES)[number]

export interface RegistryEntry {
  id: string
  kind: RegistryKind
  parentAppId: string | null
  name: string
  description: string
  url: string
  category: string | null
  icon: string | null
  hidden: boolean
  audience: RegistryAudience
  /** Set for per-user imported gadgets; null for global rows. */
  ownerUserId: string | null
  clientId: string | null
  allowedScopes: string[]
  redirectUris: string[]
  createdAt: string
  updatedAt: string
}

// What the registration form sends. parentAppId is required for a tool; redirectUris/allowedScopes
// apply only to an app. The backend validates all of this too — this shape just mirrors it.
export interface RegistryEntryInput {
  kind: RegistryKind
  name: string
  description: string
  url: string
  category?: string
  icon?: string | null
  audience: RegistryAudience
  parentAppId?: string
  allowedScopes?: string[]
  redirectUris?: string[]
}

// The POST response is a RegistryEntry, plus a one-time clientSecret when a new app's client was
// created with a confidential secret. Surfaced once to the registrant, never stored client-side.
export type RegistryCreateResult = RegistryEntry & { clientSecret?: string }

/** One-time credentials returned only when an authorized internal operator rotates Studio's secret. */
export interface StudioClientSecretResult {
  clientId: string
  clientSecret: string
}

function authHeaders(token: string | null): Record<string, string> {
  const h: Record<string, string> = { 'Content-Type': 'application/json' }
  if (token) h['Authorization'] = `Bearer ${token}`
  return h
}

const base = (apiBaseUrl: string) => `${apiBaseUrl}/api/registry/entries`

export async function listRegistryEntries(
  apiBaseUrl: string,
  token: string | null,
  kindOrFetcher?: RegistryKind | RegistryKind[] | typeof fetch,
  requestedFetcher: typeof fetch = fetch,
): Promise<RegistryEntry[]> {
  const fetcher = typeof kindOrFetcher === 'function' ? kindOrFetcher : requestedFetcher
  const kinds = typeof kindOrFetcher === 'function' || kindOrFetcher === undefined
    ? []
    : Array.isArray(kindOrFetcher) ? kindOrFetcher : [kindOrFetcher]
  const load = async (kind?: RegistryKind): Promise<RegistryEntry[]> => {
    const url = kind ? `${base(apiBaseUrl)}?kind=${encodeURIComponent(kind)}` : base(apiBaseUrl)
    const res = await fetcher(url, { credentials: 'include', headers: authHeaders(token) })
    if (!res.ok) throw new Error(`registry list ${res.status}`)
    const data = (await res.json()) as { entries: RegistryEntry[] }
    return data.entries ?? []
  }
  if (kinds.length === 0) return load()
  return (await Promise.all(kinds.map(kind => load(kind)))).flat()
}

export async function createRegistryEntry(
  apiBaseUrl: string,
  token: string | null,
  input: RegistryEntryInput,
  fetcher: typeof fetch = fetch,
): Promise<RegistryCreateResult> {
  const res = await fetcher(base(apiBaseUrl), {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(token),
    body: JSON.stringify(input),
  })
  if (!res.ok) {
    // The backend returns { error } with a plain-language message; surface it so the form can show why.
    let message = `registry create ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new Error(message)
  }
  return (await res.json()) as RegistryCreateResult
}

export async function generateStudioClientSecret(
  apiBaseUrl: string,
  token: string | null,
  fetcher: typeof fetch = fetch,
): Promise<StudioClientSecretResult> {
  const res = await fetcher(`${apiBaseUrl}/api/registry/app-studio/client-secret`, {
    method: 'POST',
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) {
    let message = `Studio client secret generation ${res.status}`
    try {
      const body = (await res.json()) as {error?: string}
      if (body?.error) message = body.error
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new Error(message)
  }
  return (await res.json()) as StudioClientSecretResult
}

// What the EDIT form may change. The identity/OAuth-binding fields (id, kind, clientId,
// allowedScopes, redirectUris) are deliberately absent: they are read-only on edit and the backend
// preserves them from the existing row regardless of what is sent.
export interface RegistryEntryUpdate {
  name?: string
  description?: string
  url?: string
  category?: string
  icon?: string | null
  hidden?: boolean
  audience?: RegistryAudience
  // App-only OAuth binding fields. Editable now: a redirectUris change re-provisions the live OAuth
  // client backend-side; allowedScopes is the registry scope ceiling. Ignored for tool/public_app.
  redirectUris?: string[]
  allowedScopes?: string[]
}

export async function updateRegistryEntry(
  apiBaseUrl: string,
  token: string | null,
  id: string,
  patch: RegistryEntryUpdate,
  fetcher: typeof fetch = fetch,
): Promise<RegistryEntry> {
  const res = await fetcher(`${base(apiBaseUrl)}/${id}`, {
    method: 'PUT',
    credentials: 'include',
    headers: authHeaders(token),
    body: JSON.stringify(patch),
  })
  if (!res.ok) {
    let message = `registry update ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new Error(message)
  }
  return (await res.json()) as RegistryEntry
}

export async function deleteRegistryEntry(apiBaseUrl: string, token: string | null, id: string, fetcher: typeof fetch = fetch): Promise<void> {
  const res = await fetcher(`${base(apiBaseUrl)}/${id}`, {
    method: 'DELETE',
    credentials: 'include',
    headers: authHeaders(token),
  })
  if (!res.ok) {
    let message = `registry delete ${res.status}`
    try {
      const body = (await res.json()) as { error?: string }
      if (body?.error) message = body.error
    } catch {
      // non-JSON error body — keep the status-based message
    }
    throw new Error(message)
  }
}
